'use client';

import { useMemo, useState } from 'react';
import { PERMISSIONS } from '@terra/shared-authorization';
import { SettingsContentPanel } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useActiveInternalNetworkDiscovery } from '../../../hooks/api/useInternalNetworkDiscoveryStatus';
import { useInternalNetworkDetail } from '../../../hooks/api/useSurfaceSettings';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  INTERNAL_NETWORK_SETTINGS_TABS,
  getInternalNetworkSettingsNavItems,
  resolveInternalNetworkSettingsActiveTab,
} from './internalNetworkSettings.constants';
import { NetworkSettingsShell } from './NetworkSettingsShell';
import { InternalGeneralSettingsPanel } from './internal/InternalGeneralSettingsPanel';
import { InternalNetworkScopeSettingsPanel } from './internal/InternalNetworkScopeSettingsPanel';
import { InternalSensorSettingsPanel } from './internal/InternalSensorSettingsPanel';
import { InternalExecutionSettingsPanel } from './internal/InternalExecutionSettingsPanel';
import { InternalCredentialsSettingsPanel } from './internal/InternalCredentialsSettingsPanel';
import { InternalDangerSettingsPanel } from './internal/InternalDangerSettingsPanel';

type InternalNetworkSettingsTabProps = {
  networkId: string | null;
};

export function InternalNetworkSettingsTab({
  networkId,
}: InternalNetworkSettingsTabProps) {
  const t = useTranslations('internalNetwork.settings');
  const tMainContent = useTranslations('proxy.mainContentArea');
  const [selectedTab, setSelectedTab] = useState<string>(
    INTERNAL_NETWORK_SETTINGS_TABS.GENERAL,
  );
  const { hasPermission } = usePermissions();
  const canManageInternalNetwork = hasPermission(
    PERMISSIONS.MANAGE_INTERNAL_NETWORK.name,
  );
  const isReadOnly = !canManageInternalNetwork;
  const { isFeatureFlagEnabled: isTunnelModeEnabled } = useFeatureFlags(
    'INTERNAL_NETWORK_TUNNEL_MODE_ENABLED',
  );
  const surfaceQuery = useInternalNetworkDetail(networkId);
  const { isDiscoveryActive } = useActiveInternalNetworkDiscovery(networkId);
  const surface = useMemo(() => {
    if (!surfaceQuery.data) {
      return null;
    }

    return {
      ...surfaceQuery.data,
      signalCategoriesInScope: surfaceQuery.data.signalCategoriesInScope ?? [],
      smokeTestEnabled: surfaceQuery.data.smokeTestEnabled ?? false,
    };
  }, [surfaceQuery.data]);
  const visibleNavItems = useMemo(
    () =>
      getInternalNetworkSettingsNavItems({
        isTunnelModeEnabled,
        canManageInternalNetwork,
      }),
    [canManageInternalNetwork, isTunnelModeEnabled],
  );
  const navItems = useMemo(
    () =>
      visibleNavItems.map((item) => ({
        value: item.value,
        label: t(`tabs.${item.labelKey}`),
      })),
    [t, visibleNavItems],
  );
  const activeTab = resolveInternalNetworkSettingsActiveTab({
    activeTab: selectedTab,
    navItems: visibleNavItems,
  });

  return (
    <NetworkSettingsShell
      title={t('title')}
      navItems={navItems}
      activeNavItem={activeTab}
      onNavItemChange={setSelectedTab}
      isLoading={surfaceQuery.isLoading}
      errorMessage={surfaceQuery.isError ? t('fetchError') : null}
      emptyStateMessage={
        networkId ? null : tMainContent('selectApplicationToGetStarted')
      }
    >
      {networkId && surface ? (
        <>
          <SettingsContentPanel
            value={INTERNAL_NETWORK_SETTINGS_TABS.GENERAL}
            activeValue={activeTab}
          >
            <InternalGeneralSettingsPanel
              surface={surface}
              networkId={networkId}
              isReadOnly={isReadOnly}
            />
          </SettingsContentPanel>
          <SettingsContentPanel
            value={INTERNAL_NETWORK_SETTINGS_TABS.NETWORK}
            activeValue={activeTab}
          >
            <InternalNetworkScopeSettingsPanel
              surface={surface}
              networkId={networkId}
              isReadOnly={isReadOnly}
              isDiscoveryActive={isDiscoveryActive}
            />
          </SettingsContentPanel>
          <SettingsContentPanel
            value={INTERNAL_NETWORK_SETTINGS_TABS.SENSOR}
            activeValue={activeTab}
          >
            <InternalSensorSettingsPanel
              networkId={networkId}
              isTunnelModeEnabled={isTunnelModeEnabled}
              isReadOnly={isReadOnly}
            />
          </SettingsContentPanel>
          <SettingsContentPanel
            value={INTERNAL_NETWORK_SETTINGS_TABS.EXECUTION}
            activeValue={activeTab}
          >
            <InternalExecutionSettingsPanel
              surface={surface}
              networkId={networkId}
              isReadOnly={isReadOnly}
              isDiscoveryActive={isDiscoveryActive}
            />
          </SettingsContentPanel>
          <SettingsContentPanel
            value={INTERNAL_NETWORK_SETTINGS_TABS.CREDENTIALS}
            activeValue={activeTab}
          >
            <InternalCredentialsSettingsPanel
              networkId={networkId}
              isReadOnly={isReadOnly}
            />
          </SettingsContentPanel>
          {canManageInternalNetwork ? (
            <SettingsContentPanel
              value={INTERNAL_NETWORK_SETTINGS_TABS.DANGER}
              activeValue={activeTab}
            >
              <InternalDangerSettingsPanel
                networkId={networkId}
                surfaceName={surface.name}
                isDiscoveryActive={isDiscoveryActive}
              />
            </SettingsContentPanel>
          ) : null}
        </>
      ) : null}
    </NetworkSettingsShell>
  );
}
